import React from 'react';
import {
  Box,
  Typography,
  Select,
  Option,
  Alert,
  CircularProgress,
  Chip
} from '@mui/joy';
import { useAllOpportunities } from '../../hooks/useOpportunities';
import type { Opportunity } from '../../types';

interface OpportunitySelectionProps {
  selectedOpportunityId?: number;
  onOpportunitySelect: (opportunity: Opportunity | null) => void;
  optional?: boolean;
}

export const OpportunitySelection: React.FC<OpportunitySelectionProps> = ({
  selectedOpportunityId,
  onOpportunitySelect,
  optional = true
}) => {
  const { data: opportunities, isLoading, error } = useAllOpportunities();

  const handleOpportunityChange = (
    _event: React.SyntheticEvent | null,
    newValue: string | null
  ) => {
    if (newValue && opportunities) {
      const selectedOpportunity = opportunities.find(opp => opp.id === parseInt(newValue));
      onOpportunitySelect(selectedOpportunity || null);
    } else {
      onOpportunitySelect(null);
    }
  };

  if (isLoading) {
    return (
      <Box>
        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
          Associated Opportunity {optional && '(Optional)'}
        </Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <CircularProgress size="sm" />
          <Typography level="body-sm">Loading opportunities...</Typography>
        </Box>
      </Box>
    );
  }

  if (error) {
    return (
      <Box>
        <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
          Associated Opportunity {optional && '(Optional)'}
        </Typography>
        <Alert color="warning">
          Error loading opportunities. Please refresh the page.
        </Alert>
      </Box>
    );
  }

  const opportunitiesList = opportunities || [];

  // Helper function to get stage display name
  const getStageDisplay = (opp: Opportunity): string => {
    if (typeof opp.stage === 'string') {
      return opp.stage;
    }
    // You might want to map numeric stages to names here
    return opp.stage_name || `Stage ${opp.stage}`;
  };

  // Helper function to get stage color
  const getStageColor = (opp: Opportunity): 'primary' | 'success' | 'warning' | 'danger' | 'neutral' => {
    const stageName = getStageDisplay(opp).toLowerCase();
    if (stageName.includes('won') || stageName.includes('closed')) return 'success';
    if (stageName.includes('lost')) return 'danger';
    if (stageName.includes('proposal') || stageName.includes('negotiation')) return 'warning';
    if (stageName.includes('qualified') || stageName.includes('discovery')) return 'primary';
    return 'neutral';
  };

  return (
    <Box>
      <Typography level="body-sm" sx={{ mb: 1, fontWeight: 'bold' }}>
        Associated Opportunity {optional && '(Optional)'}
      </Typography>
      <Select
        placeholder="Select an opportunity for this measurement..."
        value={selectedOpportunityId?.toString() || null}
        onChange={handleOpportunityChange}
        sx={{ width: '100%' }}
      >
        {opportunitiesList.map((opp) => (
          <Option key={opp.id} value={opp.id.toString()}>
            <Box sx={{ width: '100%' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <Typography level="body-sm" sx={{ fontWeight: 'medium' }}>
                  {opp.name}
                </Typography>
                <Chip size="sm" color={getStageColor(opp)} variant="soft">
                  {getStageDisplay(opp)}
                </Chip>
              </Box>
              {opp.contact_name && (
                <Typography level="body-xs" sx={{ color: 'text.secondary' }}>
                  {opp.contact_name} • ${opp.estimated_value?.toLocaleString() || opp.value?.toLocaleString() || '0'}
                </Typography>
              )}
            </Box>
          </Option>
        ))}
      </Select>
      
      {opportunitiesList.length === 0 && (
        <Alert color="neutral" sx={{ mt: 1 }}>
          No opportunities available. Create an opportunity first if you want to link it to this measurement.
        </Alert>
      )}
    </Box>
  );
};